import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AnvaQuote,
  AnvaQuoteWithJob,
  AnvaPackageWithJob,
  AnvaJobStatus,
  AnvaPackage,
} from '@flows/src/types/anva';
import {
  useAnvaQuotesV2Calculate,
  getJobResult,
  AnvaQuotesPayload,
  isAnvaQuote,
  isJobErrorResult,
} from '@flows/src/api/anva-flows/getAnvaQuotesV2';
import useAnvaFlowProductsBasketStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductsBasketStore';

// Type guard to check if a result is an AnvaPackage
const isAnvaPackage = (result: unknown): result is AnvaPackage => {
  return (
    typeof result === 'object' &&
    result !== null &&
    'name' in result &&
    'code' in result &&
    'quotes' in result &&
    Array.isArray((result as Record<string, unknown>).quotes)
  );
};

interface JobState {
  jobId: string;
  status: AnvaJobStatus;
  isLoading: boolean;
  error: string | null;
  result: AnvaQuote | AnvaPackage | null; // Package jobs return AnvaPackage, quote jobs return AnvaQuote
  packageCode?: string; // For package quotes
  isStandalone?: boolean; // For standalone insurances
  anvaProductId: string;
  index: number;
}

interface UseBasketCalculateWithPollingReturn {
  calculate: (payload: AnvaQuotesPayload) => Promise<void>;
  isCalculating: boolean;
  isGlobalLoading: boolean;
  jobStates: Record<string, JobState>;
  error: Error | null;
}

export const useBasketCalculateWithPolling = (
  sessionId: string,
): UseBasketCalculateWithPollingReturn => {
  const [jobStates, setJobStates] = useState<Record<string, JobState>>({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pollTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const pollStartTimeRef = useRef<Record<string, number>>({});

  const POLL_INTERVAL = 3000; // 3 seconds
  const POLL_TIMEOUT = 180000; // 3 minutes

  const { mutateAsync: calculateV2 } = useAnvaQuotesV2Calculate();
  const setPackages = useAnvaFlowProductsBasketStore(
    (state) => state.setPackages,
  );
  const setInsurances = useAnvaFlowProductsBasketStore(
    (state) => state.setInsurances,
  );
  const setIsBasketValidated = useAnvaFlowProductsBasketStore(
    (state) => state.setIsBasketValidated,
  );

  const isGlobalLoading = isCalculating && Object.keys(jobStates).length === 0;

  const updateJobState = useCallback(
    (jobId: string, updates: Partial<JobState>) => {
      setJobStates((prev) => ({
        ...prev,
        [jobId]: { ...prev[jobId], ...updates },
      }));
    },
    [],
  );

  const pollJobResult = useCallback(
    async (jobId: string) => {
      try {
        const jobResult = await getJobResult(jobId);

        if (jobResult.status === 'SUCCEEDED' || jobResult.status === 'FAILED') {
          if (jobResult.status === 'SUCCEEDED' && jobResult.result) {
            // Check if the result is a valid AnvaQuote
            if (isAnvaQuote(jobResult.result)) {
              // Check if the result has success: false (API error within successful job)
              if (
                jobResult.result.success === false &&
                jobResult.result.error
              ) {
                updateJobState(jobId, {
                  status: 'FAILED',
                  isLoading: false,
                  error: jobResult.result.error,
                  result: null,
                });
              } else {
                // Extract the index from the result and update the job state
                updateJobState(jobId, {
                  status: 'SUCCEEDED',
                  isLoading: false,
                  result: jobResult.result,
                  error: null,
                  index: jobResult.result.index, // Use the index from the actual result
                });
              }
            } else if (isAnvaPackage(jobResult.result)) {
              // Handle AnvaPackage results
              // Check if the package has success: false (API error within successful job)
              if (
                jobResult.result.success === false &&
                jobResult.result.error
              ) {
                updateJobState(jobId, {
                  status: 'FAILED',
                  isLoading: false,
                  error: jobResult.result.error,
                  result: null,
                });
              } else {
                // Package calculation succeeded
                updateJobState(jobId, {
                  status: 'SUCCEEDED',
                  isLoading: false,
                  result: jobResult.result,
                  error: null,
                  index: -1, // Package jobs don't have a specific index
                });
              }
            } else {
              // Handle job error result - extract actual error details
              let errorMessage = JSON.stringify(jobResult);

              if (jobResult.result && isJobErrorResult(jobResult.result)) {
                errorMessage =
                  jobResult.result.message ||
                  jobResult.result.title ||
                  JSON.stringify(jobResult);
              }

              updateJobState(jobId, {
                status: 'FAILED',
                isLoading: false,
                error: errorMessage,
                result: null,
              });
            }
          } else {
            // Extract error details from job result if available
            let errorMessage = 'NO_RESULT_AVAILABLE';

            if (jobResult.status === 'FAILED') {
              if (jobResult.result && isJobErrorResult(jobResult.result)) {
                errorMessage =
                  jobResult.result.message ||
                  jobResult.result.title ||
                  JSON.stringify(jobResult);
              } else {
                errorMessage = JSON.stringify(jobResult);
              }
            }

            updateJobState(jobId, {
              status: 'FAILED',
              isLoading: false,
              error: errorMessage,
            });
          }
          stopPolling(jobId);
          return true; // Job completed
        }

        // Job is still in progress
        updateJobState(jobId, { status: jobResult.status });
        return false; // Continue polling
      } catch (error) {
        console.error(`Error polling job ${jobId}:`, error);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        updateJobState(jobId, {
          status: 'FAILED',
          isLoading: false,
          error: errorMessage,
        });
        stopPolling(jobId);
        return true; // Stop polling on error
      }
    },
    [updateJobState],
  );

  const startPolling = useCallback(
    (jobId: string) => {
      if (pollTimeoutRef.current[jobId]) {
        clearTimeout(pollTimeoutRef.current[jobId]);
      }

      pollStartTimeRef.current[jobId] = Date.now();

      const poll = async () => {
        try {
          const elapsed = Date.now() - pollStartTimeRef.current[jobId];

          if (elapsed >= POLL_TIMEOUT) {
            updateJobState(jobId, {
              status: 'FAILED',
              isLoading: false,
              error: 'CALCULATION_TIMEOUT',
            });
            stopPolling(jobId);
            return;
          }

          const completed = await pollJobResult(jobId);

          if (!completed) {
            // Validate refs are still active before scheduling next poll
            if (pollTimeoutRef.current && pollStartTimeRef.current[jobId]) {
              pollTimeoutRef.current[jobId] = setTimeout(poll, POLL_INTERVAL);
            }
          }
        } catch (error) {
          // Handle any unhandled exceptions in the polling loop
          console.error(
            `Critical error in polling loop for job ${jobId}:`,
            error,
          );
          const errorMessage =
            error instanceof Error ? error.message : 'POLLING_LOOP_ERROR';
          updateJobState(jobId, {
            status: 'FAILED',
            isLoading: false,
            error: errorMessage,
          });
          stopPolling(jobId);
        }
      };

      // Start first poll immediately with error handling
      poll().catch((error) => {
        console.error(`Error starting poll for job ${jobId}:`, error);
        const errorMessage =
          error instanceof Error ? error.message : 'POLL_START_ERROR';
        updateJobState(jobId, {
          status: 'FAILED',
          isLoading: false,
          error: errorMessage,
        });
        stopPolling(jobId);
      });
    },
    [pollJobResult, updateJobState],
  );

  const stopPolling = useCallback((jobId: string) => {
    if (pollTimeoutRef.current[jobId]) {
      clearTimeout(pollTimeoutRef.current[jobId]);
      delete pollTimeoutRef.current[jobId];
    }
    delete pollStartTimeRef.current[jobId];
  }, []);

  // Update basket store when jobs complete
  useEffect(() => {
    const completedJobs = Object.values(jobStates).filter(
      (job) => !job.isLoading && job.result,
    );
    const failedJobs = Object.values(jobStates).filter(
      (job) => !job.isLoading && job.error,
    );

    if (completedJobs.length === 0 && failedJobs.length === 0) return;

    // Group completed results by packages and standalone insurances
    const packageQuotes: Record<string, AnvaQuote[]> = {};
    const standaloneQuotes: AnvaQuote[] = [];

    completedJobs.forEach((job) => {
      if (job.result) {
        if (job.packageCode) {
          if (!packageQuotes[job.packageCode]) {
            packageQuotes[job.packageCode] = [];
          }
          // Handle package-level jobs that return AnvaPackage
          if ('quotes' in job.result && Array.isArray(job.result.quotes)) {
            // This is an AnvaPackage
            packageQuotes[job.packageCode].push(...job.result.quotes);
          } else if ('anva_product_id' in job.result) {
            // This is an AnvaQuote (individual quote in package)
            packageQuotes[job.packageCode].push(job.result);
          }
        } else if (job.isStandalone) {
          // Individual quote jobs should always return single AnvaQuote
          if ('anva_product_id' in job.result) {
            standaloneQuotes.push(job.result);
          }
        }
      }
    });

    // Update packages in store with completed quotes
    const currentPackages = useAnvaFlowProductsBasketStore.getState().packages;
    const updatedPackages = currentPackages.map((pkg) => {
      const completedPackageQuotes = packageQuotes[pkg.code] || [];

      // Find the package-level job result for this package
      const packageJobResult = completedJobs.find(
        (job) => job.packageCode === pkg.code && isAnvaPackage(job.result),
      );

      if (completedPackageQuotes.length > 0) {
        // Create a copy of completed quotes for fallback matching to avoid double-matching
        const remainingCompletedQuotes = [...completedPackageQuotes];

        const updatedPackage = {
          ...pkg,
          quotes: pkg.quotes.map((quote) => {
            // First try to match by both anva_product_id and index
            const updatedQuote = completedPackageQuotes.find(
              (completed) =>
                completed.anva_product_id === quote.anva_product_id &&
                completed.index === quote.index,
            );

            if (updatedQuote) {
              // Remove from remaining quotes to avoid double-matching
              const index = remainingCompletedQuotes.findIndex(
                (q) =>
                  q.anva_product_id === updatedQuote.anva_product_id &&
                  q.index === updatedQuote.index,
              );
              if (index > -1) remainingCompletedQuotes.splice(index, 1);
              return updatedQuote;
            }

            // If no exact match found, try to match by anva_product_id only from remaining quotes
            // This handles cases where the index might have changed between calculations
            const fallbackQuote = remainingCompletedQuotes.find(
              (completed) =>
                completed.anva_product_id === quote.anva_product_id,
            );

            if (fallbackQuote) {
              // Remove from remaining quotes to avoid double-matching
              const index = remainingCompletedQuotes.findIndex(
                (q) =>
                  q.anva_product_id === fallbackQuote.anva_product_id &&
                  q.index === fallbackQuote.index,
              );
              if (index > -1) remainingCompletedQuotes.splice(index, 1);
              return fallbackQuote;
            }

            return quote;
          }),
        };

        // Update package-level properties from AnvaPackage result if available
        if (packageJobResult && isAnvaPackage(packageJobResult.result)) {
          const anvaPackageResult = packageJobResult.result;
          return {
            ...updatedPackage,
            total_premium: anvaPackageResult.total_premium,
            discount: anvaPackageResult.discount,
            discount_percentage: anvaPackageResult.discount_percentage,
          };
        }

        return updatedPackage;
      }

      // Even if no quotes, still update package-level properties if we have a package result
      if (packageJobResult && isAnvaPackage(packageJobResult.result)) {
        const anvaPackageResult = packageJobResult.result;
        return {
          ...pkg,
          total_premium: anvaPackageResult.total_premium,
          discount: anvaPackageResult.discount,
          discount_percentage: anvaPackageResult.discount_percentage,
        };
      }

      return pkg;
    });

    // Update standalone insurances
    const currentInsurances =
      useAnvaFlowProductsBasketStore.getState().insurances;
    // Create a copy of completed quotes for fallback matching to avoid double-matching
    const remainingStandaloneQuotes = [...standaloneQuotes];

    const updatedInsurances = currentInsurances.map((insurance) => {
      // First try to match by both anva_product_id and index
      const updatedInsurance = standaloneQuotes.find(
        (completed) =>
          completed.anva_product_id === insurance.anva_product_id &&
          completed.index === insurance.index,
      );

      if (updatedInsurance) {
        // Remove from remaining quotes to avoid double-matching
        const index = remainingStandaloneQuotes.findIndex(
          (q) =>
            q.anva_product_id === updatedInsurance.anva_product_id &&
            q.index === updatedInsurance.index,
        );
        if (index > -1) remainingStandaloneQuotes.splice(index, 1);
        const hasPaymentInterval =
          updatedInsurance.payment_interval !== undefined &&
          updatedInsurance.payment_interval !== null;
        const hasStartDate =
          updatedInsurance.start_date !== undefined &&
          updatedInsurance.start_date !== null;
        const hasCollectionMethod =
          updatedInsurance.anva_collection_method_code !== undefined &&
          updatedInsurance.anva_collection_method_code !== null;

        return {
          ...insurance,
          ...updatedInsurance,
          payment_interval: hasPaymentInterval
            ? updatedInsurance.payment_interval
            : insurance.payment_interval,
          start_date: hasStartDate
            ? updatedInsurance.start_date
            : insurance.start_date,
          anva_collection_method_code: hasCollectionMethod
            ? updatedInsurance.anva_collection_method_code
            : insurance.anva_collection_method_code,
        };
      }

      // If no exact match found, try to match by anva_product_id only from remaining quotes
      // This handles cases where the index might have changed between calculations
      const fallbackInsurance = remainingStandaloneQuotes.find(
        (completed) => completed.anva_product_id === insurance.anva_product_id,
      );

      if (fallbackInsurance) {
        // Remove from remaining quotes to avoid double-matching
        const index = remainingStandaloneQuotes.findIndex(
          (q) =>
            q.anva_product_id === fallbackInsurance.anva_product_id &&
            q.index === fallbackInsurance.index,
        );
        if (index > -1) remainingStandaloneQuotes.splice(index, 1);
        const hasPaymentInterval =
          fallbackInsurance.payment_interval !== undefined &&
          fallbackInsurance.payment_interval !== null;
        const hasStartDate =
          fallbackInsurance.start_date !== undefined &&
          fallbackInsurance.start_date !== null;
        const hasCollectionMethod =
          fallbackInsurance.anva_collection_method_code !== undefined &&
          fallbackInsurance.anva_collection_method_code !== null;

        return {
          ...insurance,
          ...fallbackInsurance,
          payment_interval: hasPaymentInterval
            ? fallbackInsurance.payment_interval
            : insurance.payment_interval,
          start_date: hasStartDate
            ? fallbackInsurance.start_date
            : insurance.start_date,
          anva_collection_method_code: hasCollectionMethod
            ? fallbackInsurance.anva_collection_method_code
            : insurance.anva_collection_method_code,
        };
      }

      return insurance;
    });

    // Only update store if we have changes
    if (Object.keys(packageQuotes).length > 0) {
      setPackages(updatedPackages);
    }
    if (standaloneQuotes.length > 0) {
      setInsurances(updatedInsurances);
    }

    // Mark basket as validated when all jobs are done (success or failure)
    const allJobsDone = Object.values(jobStates).every((job) => !job.isLoading);
    if (allJobsDone && Object.keys(jobStates).length > 0) {
      setIsBasketValidated(true);
      setIsCalculating(false);

      // Clear only successful job states after a few seconds, keep error states persistent
      setTimeout(() => {
        setJobStates((prevStates) => {
          const failedStates: Record<string, JobState> = {};

          // Keep only the failed job states
          Object.entries(prevStates).forEach(([jobId, jobState]) => {
            if (jobState.error || jobState.status === 'FAILED') {
              failedStates[jobId] = jobState;
            }
          });

          return failedStates;
        });
      }, 3000);
    }
  }, [jobStates, setPackages, setInsurances, setIsBasketValidated]);

  const calculate = useCallback(
    async (payload: AnvaQuotesPayload) => {
      try {
        setError(null);
        // set calculating if there are no packages or insurances (global loading indicator)
        setIsCalculating(
          !(
            payload.chosen_insurance_packages?.length ||
            payload.chosen_insurance_policies?.length
          ),
        );
        setIsBasketValidated(false);

        // Clear previous job states
        const currentJobStates = Object.keys(jobStates);
        currentJobStates.forEach(stopPolling);
        setJobStates({});

        const responseData = await calculateV2({
          sessionId,
          payload,
        });

        // Initialize job states for each quote with job info
        const newJobStates: Record<string, JobState> = {};

        // Handle individual quote jobs (from responseData.quotes)
        responseData.quotes.forEach((quoteWithJob: AnvaQuoteWithJob) => {
          const jobId = quoteWithJob.job.job_id;

          // All quotes in responseData.quotes are standalone insurance jobs
          newJobStates[jobId] = {
            jobId,
            status: quoteWithJob.job.status,
            isLoading: true,
            error: null,
            result: null,
            packageCode: undefined,
            isStandalone: true,
            anvaProductId: quoteWithJob.anva_product_id,
            index: 0, // Standalone quotes don't have index in this context - will be updated from result
          };
        });

        // Handle package-level jobs
        if (responseData.packages) {
          responseData.packages.forEach(
            (packageWithJob: AnvaPackageWithJob) => {
              const jobId = packageWithJob.job.job_id;
              const packageCode = packageWithJob.code;

              newJobStates[jobId] = {
                jobId,
                status: packageWithJob.job.status,
                isLoading: true,
                error: null,
                result: null,
                packageCode,
                isStandalone: false,
                anvaProductId: '', // Package jobs don't have a specific product ID
                index: -1, // Temporary placeholder, package jobs may have multiple quotes with different indices
              };
            },
          );
        }

        setJobStates(newJobStates);

        // Start polling for each job
        Object.keys(newJobStates).forEach((jobId) => {
          startPolling(jobId);
        });
      } catch (error) {
        console.error('Basket calculation error:', error);
        setError(error as Error);
        setIsCalculating(false);
      }
    },
    [sessionId, calculateV2, stopPolling, startPolling, setIsBasketValidated],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.keys(pollTimeoutRef.current).forEach((jobId) => {
        stopPolling(jobId);
      });
    };
  }, [stopPolling]);

  // Additional safeguard to detect stuck polling jobs
  useEffect(() => {
    const checkStuckJobs = () => {
      const now = Date.now();
      Object.entries(pollStartTimeRef.current).forEach(([jobId, startTime]) => {
        const elapsed = now - startTime;
        if (elapsed > POLL_TIMEOUT + 30000) {
          // 30 seconds grace period
          console.warn(`Detected stuck polling job ${jobId}, cleaning up...`);
          stopPolling(jobId);
          updateJobState(jobId, {
            status: 'FAILED',
            isLoading: false,
            error: 'POLLING_STUCK_CLEANUP',
          });
        }
      });
    };

    const cleanupInterval = setInterval(checkStuckJobs, 30000); // Check every 30 seconds
    return () => clearInterval(cleanupInterval);
  }, [stopPolling, updateJobState]);

  return {
    calculate,
    isCalculating,
    isGlobalLoading,
    jobStates,
    error,
  };
};
